import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

import useNotification from "src/hooks/notification";
import { getScanFields, findItemsByMobileLocation } from "src/utils/assetUtils";
import IWOBulkStagingOrReturnModal from "./IWOBulkStagingOrReturnModal";
import IWOMasterScanOutModal from "./IWOMasterScanOutModal";
import StagingOrReturnModal from "./ItemDetails/StagingOrReturnModal";

export default function IWOMasterScanOrEnterModal({
  iwoItems,
  projectId,
  internalWorkOrder,
  onClose,
  onScanComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [openScanModal, setOpenScanModal] = useState(false);
  const [bulkItems, setBulkItems] = useState(null);
  const [singleMatch, setSingleMatch] = useState(null);
  const { notifyError } = useNotification();

  const handleManualSubmit = () => {
    const value = manualInput.trim();
    if (!value) {
      notifyError("Please enter a value");
      return;
    }

    setLoading(true);

    const activeItems = iwoItems.filter(
      item => item.asset && item.asset.type !== "Kit" && !item.completedStatus
    );

    for (const item of activeItems) {
      const scanFields = getScanFields(item.asset);
      const scanField = scanFields.find(([fieldValue]) => fieldValue === value);
      if (scanField) {
        setLoading(false);
        setSingleMatch(item);
        return;
      }
    }

    const mobileMatches = findItemsByMobileLocation(activeItems, value);
    if (mobileMatches.length > 0) {
      if (mobileMatches.length === 1) {
        setLoading(false);
        setSingleMatch(mobileMatches[0]);
        return;
      }

      if (mobileMatches.some(item => item.nonAllocating)) {
        notifyError(
          "Non-allocating items matched by location must be processed individually so you can choose the current source location."
        );
        setLoading(false);
        return;
      }

      setLoading(false);
      setBulkItems(mobileMatches);
      return;
    }

    notifyError(
      "No items matched. Please confirm you are entering an asset identifier or mobile warehouse location for this work order."
    );
    setLoading(false);
  };

  const handleKeyPress = event => {
    if (event.key === "Enter" && !loading) {
      handleManualSubmit();
    }
  };

  if (bulkItems) {
    return (
      <IWOBulkStagingOrReturnModal
        items={bulkItems}
        projectId={projectId}
        internalWorkOrder={internalWorkOrder}
        onClose={onClose}
        onSuccess={onScanComplete}
      />
    );
  }

  if (singleMatch) {
    const mode = singleMatch.pickedStatus ? "return" : "staging";
    return (
      <StagingOrReturnModal
        open
        mode={mode}
        asset={singleMatch.asset}
        item={singleMatch}
        internalWorkOrder={internalWorkOrder}
        onClose={onClose}
        onSuccess={onScanComplete}
      />
    );
  }

  if (openScanModal) {
    return (
      <IWOMasterScanOutModal
        iwoItems={iwoItems}
        projectId={projectId}
        internalWorkOrder={internalWorkOrder}
        onClose={onClose}
        onScanComplete={onScanComplete}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth={false} sx={{ "& .MuiDialog-paper": { width: 400 } }}>
      <DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <Typography align="center" fontWeight={500} fontSize={20} color="text.primary">
          Scan Items
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2} align="center">
          Scan an item (Item ID, barcode, or location tag) to match it to this order
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            variant="outlined"
            value={manualInput}
            onChange={e => setManualInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            autoFocus
          />
          {loading && <LinearProgress />}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setOpenScanModal(true)}
          color="primary"
          variant="outlined"
          disabled={loading}
          startIcon={<QrCodeScannerIcon />}
        >
          Scan a Barcode
        </Button>
        <Button
          onClick={handleManualSubmit}
          color="primary"
          variant="contained"
          disabled={loading || !manualInput.trim()}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
